/**
 * forexService — read-only client for the Phase 11 forex bridge.
 *
 * Mirrors `simulationService.ts` patterns. ALL endpoints are GET. There is
 * no write client here, and no place in this file constructs an OANDA token.
 *
 * Phase 11 invariants:
 *   - This client never sends or receives an OANDA API token.
 *   - LIVE_LIMITED / LIVE_FULL are reported as 'BLOCKED' by the bridge.
 *   - Errors surface as thrown Error objects; the UI shows a banner.
 */

import { apiV1Base } from '../lib/apiConfig';
const BASE_URL =
  apiV1Base();

// ─────────────────────────────────────────────────────────────────────
//  TYPES (mirror the backend response contract)
// ─────────────────────────────────────────────────────────────────────

export type ForexLiveStatus = 'BLOCKED';

export interface ForexAccountSnapshot {
  account_id: string;
  broker: 'OANDA';
  account_type: 'forex_paper';
  environment: 'practice';
  system_state: 'SAFE_MODE' | 'READ_ONLY' | 'PAPER_TRADING';
  currency: string;
  balance: number;
  equity: number;
  margin_used: number;
  margin_available: number;
  open_positions_count: number;
  leverage: number;
  oanda_configured: boolean;
  live_trading_status: ForexLiveStatus;
  live_limited_status: ForexLiveStatus;
  live_full_status: ForexLiveStatus;
}

export interface ForexPosition {
  symbol: string;
  side: 'buy' | 'sell';
  units: number;
  entry_price: number;
  current_price?: number;
  unrealized_pnl?: number;
  margin_used?: number;
}

export interface ForexOrder {
  symbol: string;
  side: 'buy' | 'sell';
  order_type: string;
  units: number;
  client_order_id?: string;
  price?: number;
  stop_loss?: number;
  take_profit?: number;
}

export interface ForexQuote {
  symbol: string;
  bid: number;
  ask: number;
  spread: number;
  spread_pips: number;
  timestamp: string;
}

export interface ForexHealth {
  api_reachable: boolean;
  latency_ms: number;
  error_rate_pct: number;
  last_successful_sync: string | null;
  state: string;
  practice_url: string;
  oanda_configured: boolean;
}

export interface ForexSystemStateSnapshot {
  current_state: 'SAFE_MODE' | 'READ_ONLY' | 'PAPER_TRADING';
  default_state: 'SAFE_MODE';
  blocked_states: string[];
  live_trading_status: ForexLiveStatus;
}

export interface ForexPaperSession {
  session_id: number | null;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  required_days: number;
  elapsed_days: number;
  metrics: unknown | null;
  gates: unknown[];
  validation_complete: boolean;
  validation_status_label: string;
}

export interface ForexLiveReadiness {
  review_id: number | null;
  status: string;
  live_limited_decision: ForexLiveStatus;
  live_full_decision: ForexLiveStatus;
  blocked_reasons: string[];
  approval_count: number;
  required_approvals: number;
}

interface GatewayEnvelope<T> {
  ok: boolean;
  data: T | null;
  error: { message: string; code: string } | null;
}

async function fetchForex<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'GET',
    headers: {
      'X-API-Key': localStorage.getItem('auth_token') || 'dev-admin-key',
    },
  });
  if (!res.ok) {
    throw new Error(`Forex bridge error ${res.status} on ${path}`);
  }
  const body = (await res.json()) as GatewayEnvelope<T>;
  if (!body.ok || body.data === null) {
    throw new Error(body.error?.message ?? `Forex bridge error on ${path}`);
  }
  return body.data;
}

export async function fetchForexAccount(): Promise<ForexAccountSnapshot> {
  return fetchForex<ForexAccountSnapshot>('/forex/account');
}

export async function fetchForexPositions(): Promise<ForexPosition[]> {
  const data = await fetchForex<{ positions: ForexPosition[] }>('/forex/positions');
  return data.positions ?? [];
}

export async function fetchForexOrders(): Promise<ForexOrder[]> {
  const data = await fetchForex<{ orders: ForexOrder[] }>('/forex/orders');
  return data.orders ?? [];
}

export async function fetchForexPrices(symbols?: string[]): Promise<ForexQuote[]> {
  const query = symbols && symbols.length > 0 ? `?symbols=${symbols.join(',')}` : '';
  const data = await fetchForex<{ quotes: ForexQuote[] }>(`/forex/prices${query}`);
  return data.quotes ?? [];
}

export async function fetchForexHealth(): Promise<ForexHealth> {
  return fetchForex<ForexHealth>('/forex/health');
}

export async function fetchForexSystemState(): Promise<ForexSystemStateSnapshot> {
  return fetchForex<ForexSystemStateSnapshot>('/forex/system-state');
}

export async function fetchForexPaperSession(): Promise<ForexPaperSession> {
  return fetchForex<ForexPaperSession>('/forex/paper-session');
}

export async function fetchForexLiveReadiness(): Promise<ForexLiveReadiness> {
  return fetchForex<ForexLiveReadiness>('/forex/live-readiness');
}

export interface ForexBundle {
  account: ForexAccountSnapshot;
  positions: ForexPosition[];
  orders: ForexOrder[];
  prices: ForexQuote[];
  health: ForexHealth;
  systemState: ForexSystemStateSnapshot;
  paperSession: ForexPaperSession;
  liveReadiness: ForexLiveReadiness;
}

export async function fetchForexBundle(): Promise<ForexBundle> {
  const [account, positions, orders, prices, health, systemState, paperSession, liveReadiness] =
    await Promise.all([
      fetchForexAccount(),
      fetchForexPositions(),
      fetchForexOrders(),
      fetchForexPrices(),
      fetchForexHealth(),
      fetchForexSystemState(),
      fetchForexPaperSession(),
      fetchForexLiveReadiness(),
    ]);
  return { account, positions, orders, prices, health, systemState, paperSession, liveReadiness };
}

// ─────────────────────────────────────────────────────────────────────
//  REGIME STATE & PERFORMANCE (NEW)
// ─────────────────────────────────────────────────────────────────────

export interface RegimeState {
  regime_state: 'trending' | 'ranging' | 'volatile' | 'unknown';
  regime_confidence: number;
  last_signal_time: string | null;
  last_signal_confidence: number | null;
  last_signal_direction: 'long' | 'short' | null;
  active_profile: string;
  strategy_type: string;
  last_tick_at: string;
  timestamp: string;
}

export interface ForexSignal {
  timestamp: string;
  symbol: string;
  direction: 'long' | 'short';
  confidence: number;
  regime_state: string;
  regime_confidence: number;
}

export interface DailyPerformance {
  date: string;
  trades: number;
  wins: number;
  losses: number;
  total_pnl: number;
  gross_wins: number;
  gross_losses: number;
  max_win: number;
  max_loss: number;
  win_rate?: number;
  avg_win?: number;
  avg_loss?: number;
  symbols: string[];
}

// 2026-05-15 — Stop 405 spam from /forex/regime/*.
//
// The Phase 11 read-only bridge exposes no GET handler for /forex/regime/*;
// the backend registers a POST catch-all that rejects writes with
// FOREX_WRITE_BLOCKED. Calling these from the UI returned 405 (Allow: POST)
// on every mount of RegimeStateWidget / SignalHistoryTable / DailyPnLChart,
// flooding the console and nginx access log from every page where a Forex
// widget rendered (including stale references after navigation).
//
// We short-circuit here so the widgets render their existing 'unavailable'
// state from the thrown error, with zero traffic on the wire. When a
// proper GET contract ships on the backend, restore the fetchForex(...)
// calls below.
const REGIME_UNAVAILABLE_MSG =
  'Forex regime telemetry is unavailable on this build.';

export async function fetchRegimeState(): Promise<RegimeState> {
  throw new Error(REGIME_UNAVAILABLE_MSG);
}

export async function fetchRecentSignals(limit: number = 50): Promise<ForexSignal[]> {
  void limit;
  throw new Error(REGIME_UNAVAILABLE_MSG);
}

export async function fetchDailyPerformance(days: number = 30): Promise<DailyPerformance[]> {
  void days;
  throw new Error(REGIME_UNAVAILABLE_MSG);
}

export async function fetchPerformanceSummary(): Promise<string> {
  throw new Error(REGIME_UNAVAILABLE_MSG);
}
